import { MAX_PRACTICE_SLOTS } from '../domain/raidPracticeSlots.js';
import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { useEffect, useRef, useState } from 'react';
import { MarqueeText } from '../../../shared/components/MarqueeText.jsx';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { useRaidPractice } from '../hooks/useRaidPractice.js';
import { RaidPracticeAdminBoard } from '../shared/RaidPracticeAdminBoard.jsx';
import { RaidPracticeEditor } from '../shared/RaidPracticeEditor.jsx';
import { RaidPracticeSlotCard } from '../shared/RaidPracticeSlotCard.jsx';
import { RaidPracticeThreeHitEditor } from '../shared/RaidPracticeThreeHitEditor.jsx';
import { RAID_PRACTICE_ACTION_LABELS, practiceSlotDeleteConfirmation } from '../shared/raidPracticeActionLabels.js';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

const STATUS_LABELS = { draft: '설정 중', active: '활성', closed: '종료' };

export function MobileRaidPractice() {
  const [practiceView, setPracticeView] = useState('mine');
  const practice = useRaidPractice({ enabled: practiceView === 'mine' });
  const [editor, setEditor] = useState(null);
  const [threeHitOpen, setThreeHitOpen] = useState(false);
  const [compactView, setCompactView] = useState(false);
  const season = practice.selectedSeason;
  const boss = practice.selectedBoss;
  const railRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const scrollFrameRef = useRef(0);
  const selectedFromScrollRef = useRef(0);

  useEffect(() => {
    const rail = railRef.current;
    const active = rail?.querySelector('[data-active="true"]');
    if (!rail || !active) return;
    if (selectedFromScrollRef.current === Number(practice.selectedBossNo)) {
      selectedFromScrollRef.current = 0;
      return;
    }
    const left = active.offsetLeft - (rail.clientWidth - active.clientWidth) / 2;
    rail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [compactView, practice.selectedBossNo, season?.seasonNo]);

  useEffect(() => () => {
    window.clearTimeout(scrollTimerRef.current);
    if (scrollFrameRef.current) window.cancelAnimationFrame(scrollFrameRef.current);
  }, []);

  const closeEditor = () => setEditor(null);
  const closeAllEditors = () => {
    setEditor(null);
    setThreeHitOpen(false);
  };
  const addSlot = () => {
    if (!practice.nextSlotNo) return;
    setEditor({ entry: null, slotNo: practice.nextSlotNo });
  };
  const editSlot = (entry) => setEditor({ entry, slotNo: entry?.slotNo || entry?.manual?.slotNo || 1 });
  const deleteSlot = async (id, slotNo) => {
    if (!window.confirm(practiceSlotDeleteConfirmation(slotNo))) return false;
    return practice.remove(id, slotNo);
  };
  const selectBoss = (bossNo) => {
    closeAllEditors();
    practice.selectBoss(bossNo);
  };
  const selectCenteredBoss = () => {
    const rail = railRef.current;
    if (!rail) return;
    const center = rail.scrollLeft + rail.clientWidth / 2;
    const closest = [...rail.querySelectorAll('[data-boss-no]')]
      .map((card) => ({ card, distance: Math.abs(card.offsetLeft + card.clientWidth / 2 - center) }))
      .sort((a, b) => a.distance - b.distance)[0]?.card;
    const bossNo = Number(closest?.getAttribute('data-boss-no'));
    if (!Number.isInteger(bossNo) || bossNo === Number(practice.selectedBossNo)) return;
    selectedFromScrollRef.current = bossNo;
    selectBoss(bossNo);
  };
  const queueCenteredBossSelection = () => {
    if (scrollFrameRef.current) return;
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = 0;
      selectCenteredBoss();
    });
  };
  const handleRailScroll = () => {
    queueCenteredBossSelection();
    window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(selectCenteredBoss, 36);
  };
  const finishRailInteraction = () => {
    queueCenteredBossSelection();
    window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(selectCenteredBoss, 0);
  };
  const togglePracticeView = () => {
    closeAllEditors();
    setPracticeView((current) => (current === 'mine' ? 'union' : 'mine'));
  };

  const viewSwitch = (
    <button
      className="shared-view-switch practice-view-switch"
      type="button"
      aria-pressed={practiceView === 'union'}
      onClick={togglePracticeView}
    >
      {practiceView === 'mine' ? '전체 멤버 보기' : '내 모의전 보기'}
    </button>
  );

  if (practiceView === 'union') {
    return (
      <section className="mobile-raid-practice practice-admin-host mobile">
        <RaidPracticeAdminBoard mobile viewSwitch={viewSwitch} />
      </section>
    );
  }
  if (practice.loading) return <LoadingState label="내 모의전 동기화 중" variant="card" />;
  if (practice.errorMessage) return <section className="mobile-raid-practice-state error"><strong>모의전을 불러오지 못했습니다.</strong><p>{practice.errorMessage}</p><button type="button" onClick={() => practice.refresh()}>다시 시도</button></section>;
  if (!season) return <section className="mobile-raid-practice-state"><strong>표시할 차수 없음</strong><p>차수와 보스 설정을 먼저 저장해주세요.</p></section>;

  return (
    <section
      className={`mobile-raid-practice mobile-practice-desktop-parity practice-original-v2 ${compactView ? 'mobile-practice-compact-view' : ''}`}
      aria-label="내 모의전"
    >
      <section className="mobile-practice-parity-heading">
        <div className="mobile-practice-parity-heading-top">
          <div className="mobile-practice-parity-title">
            <h1>내 모의전</h1>
          </div>
          <div className="mobile-practice-parity-heading-actions">
            {viewSwitch}
            <button
              type="button"
              className="practice-compact-view-toggle"
              aria-pressed={compactView}
              onClick={() => setCompactView((current) => !current)}
            >
              {compactView ? '기본보기' : '간편보기'}
            </button>
          </div>
        </div>
        <div className="mobile-practice-parity-controls">
          <label>
            <span>조회 차수</span>
            <select
              value={season.seasonNo}
              onChange={(event) => {
                closeAllEditors();
                practice.selectSeason(event.target.value);
              }}
            >
              {practice.seasons.map((item) => (
                <option key={item.seasonNo} value={item.seasonNo}>
                  {item.seasonNo}차 · {STATUS_LABELS[item.status] || item.status}
                </option>
              ))}
            </select>
          </label>
          <RefreshIconButton
            className="mobile-practice-parity-refresh"
            onClick={() => practice.refresh(season.seasonNo)}
          />
        </div>
      </section>

      {compactView ? (
        <section className="mobile-practice-compact-boss-selector" aria-label="모의전 보스 선택">
          <div className="mobile-practice-compact-boss-list">
            {practice.bossSummaries.map((item) => {
              const active = boss?.bossNo === item.bossNo;
              return (
                <button
                  type="button"
                  className="mobile-practice-compact-boss-item"
                  key={item.bossNo}
                  data-active={active ? 'true' : 'false'}
                  aria-pressed={active}
                  onClick={() => selectBoss(item.bossNo)}
                >
                  <span
                    className="mobile-practice-compact-boss-image"
                    style={{ backgroundImage: item.imageUrl ? `url(${siteImageUrl(item.imageUrl)})` : undefined }}
                  />
                  <span className="mobile-practice-compact-boss-copy">
                    <span><ElementIcon element={item.element} /><MarqueeText text={bossDisplayName(item)} /></span>
                    <small>슬롯 {item.practiceCount || 0}/{MAX_PRACTICE_SLOTS} · 실전 {item.actualCount || 0}타</small>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="mobile-practice-parity-carousel" aria-label="모의전 보스 선택">
          <div
            className="mobile-practice-parity-rail"
            ref={railRef}
            onScroll={handleRailScroll}
            onPointerUp={finishRailInteraction}
            onPointerCancel={finishRailInteraction}
            onTouchEnd={finishRailInteraction}
          >
            {practice.bossSummaries.map((item) => {
              const active = boss?.bossNo === item.bossNo;
              return (
                <button
                  type="button"
                  key={item.bossNo}
                  data-boss-no={item.bossNo}
                  data-active={active ? 'true' : 'false'}
                  aria-pressed={active}
                  onClick={() => selectBoss(item.bossNo)}
                >
                  <div
                    className="mobile-practice-parity-boss-image"
                    style={{ backgroundImage: item.imageUrl ? `url(${siteImageUrl(item.imageUrl)})` : undefined }}
                  />
                  <div className="mobile-practice-parity-boss-meta">
                    <div><ElementIcon element={item.element} /><MarqueeText text={bossDisplayName(item)} /></div>
                    <small>슬롯 {item.practiceCount || 0}</small>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {boss ? (
        <>
          {!compactView ? (
            <section
              className="mobile-practice-parity-hero"
              style={{ backgroundImage: boss.imageUrl ? `linear-gradient(90deg,rgba(7,12,18,.99) 0%,rgba(7,12,18,.94) 43%,rgba(12,23,34,.45) 72%,rgba(6,10,16,.12) 100%), url(${siteImageUrl(boss.imageUrl)})` : undefined }}
            >
              <div className="mobile-practice-parity-hero-copy">
                <h2>{bossDisplayName(boss)}</h2>
                <div className="mobile-practice-parity-weak-point"><b>WEAK POINT</b><ElementIcon element={boss.element} /></div>
                <p>{boss.comment || '관리자 코멘트가 없습니다.'}</p>
              </div>
              <dl>
                <div><dt>모의전 기록</dt><dd>{boss.practiceCount || 0}</dd></div>
                <div><dt>실전 기록</dt><dd>{boss.actualCount || 0}</dd></div>
              </dl>
            </section>
          ) : null}

          <section className="mobile-practice-parity-slots">
            <div className="mobile-practice-parity-toolbar">
              <div><strong>{bossDisplayName(boss)} 모의전 슬롯</strong></div>
              <div className="mobile-practice-parity-toolbar-actions">
                {practice.hasActualRecords ? <button type="button" className={`raid-practice-only-toggle ${practice.practiceOnly ? 'active' : ''}`.trim()} aria-pressed={practice.practiceOnly} title="실전 기록을 빼고 등록된 모의전 슬롯만 봅니다." onClick={() => practice.setPracticeOnly(!practice.practiceOnly)}>{practice.practiceOnly ? '실전 반영 보기' : '모의전 보기'}</button> : null}
                <button
                  type="button"
                  className="raid-practice-three-hit-open"
                  onClick={() => {
                    closeEditor();
                    setThreeHitOpen(true);
                  }}
                >
                  3타 작성
                </button>
              </div>
            </div>
            <div className="mobile-practice-parity-slot-grid">
              {practice.visibleSlots.map((entry) => (
                <RaidPracticeSlotCard
                  key={entry.key}
                  entry={entry}
                  mobile
                  working={practice.working}
                  onEdit={editSlot}
                  onDelete={deleteSlot}
                />
              ))}
              {practice.nextSlotNo ? <button
                type="button"
                className="mobile-practice-empty-slot-v2"
                aria-label={`${practice.nextSlotNo}번 슬롯 추가`}
                title={`${practice.nextSlotNo}번 슬롯 추가`}
                onClick={addSlot}
              >
                <span aria-hidden="true">+</span>
                <strong>{RAID_PRACTICE_ACTION_LABELS.add}</strong>
              </button> : null}
            </div>
          </section>
        </>
      ) : (
        <div className="mobile-raid-practice-empty"><strong>보스 설정 없음</strong><p>선택한 차수의 보스 정보를 저장해주세요.</p></div>
      )}

      {practice.notice.message ? <p className={`mobile-raid-practice-notice ${practice.notice.type}`}>{practice.notice.message}</p> : null}
      {editor && boss ? (
        <RaidPracticeEditor
          entry={editor.entry}
          slotNo={editor.slotNo}
          boss={boss}
          seasonNo={season.seasonNo}
          nickname={practice.viewer.nickname}
          unionId={practice.unionId}
          mobile
          working={practice.working}
          notice={practice.notice}
          onSave={practice.save}
          onDelete={practice.remove}
          onClose={closeEditor}
        />
      ) : null}
      {threeHitOpen ? (
        <RaidPracticeThreeHitEditor
          bosses={practice.bossSummaries}
          initialBossNo={practice.selectedBossNo}
          seasonNo={season.seasonNo}
          nickname={practice.viewer.nickname}
          mobile
          working={practice.working}
          onSave={practice.saveThreeHits}
          onClose={() => setThreeHitOpen(false)}
        />
      ) : null}
    </section>
  );
}
